import type {
  PaymentInvoiceListResponse,
  PaymentInvoiceDetailResponse,
  PaymentInvoiceListParams,
  PaymentInvoiceCompetenceResponse,
  PaymentInvoiceCompetenceParams,
} from '../../types/vida/payment/paymentType';
import { VIDA_PAYMENT_ROUTES } from '../requestsService';

export class PaymentService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Fetches invoice list (card summary + paginated list).
   * Backend: /v1/bff/vida/lista-faturas
   */
  async getPaymentInvoiceHistory(
    params: PaymentInvoiceListParams
  ): Promise<PaymentInvoiceListResponse> {
    const {
      identContrato,
      competenciaFatura,
      identSubestipulante,
      tipoFatura,
      idEndosso,
      codFormaCobranca,
      origemFront,
      pSitFatura,
      page = 0,
      size = 6,
    } = params;

    const searchParams = new URLSearchParams();
    searchParams.append('identContrato', String(identContrato));
    searchParams.append('competenciaFatura', competenciaFatura);

    if (identSubestipulante !== undefined) {
      searchParams.append('identSubestipulante', String(identSubestipulante));
    }
    if (tipoFatura) {
      searchParams.append('tipoFatura', tipoFatura);
    }
    if (idEndosso !== undefined) {
      searchParams.append('idEndosso', String(idEndosso));
    }
    if (codFormaCobranca !== undefined) {
      searchParams.append('codFormaCobranca', String(codFormaCobranca));
    }
    if (origemFront) {
      searchParams.append('origemFront', origemFront);
    }
    if (pSitFatura) {
      searchParams.append('pSitFatura', pSitFatura);
    }
    if (page !== undefined) {
      searchParams.append('page', String(page));
    }
    if (size !== undefined) {
      searchParams.append('size', String(size));
    }

    const url = `${VIDA_PAYMENT_ROUTES.GET_PAYMENT_INVOICE_HISTORY}?${searchParams.toString()}`;

    return this.fetch<PaymentInvoiceListResponse>(url, {
      method: 'GET',
    });
  }

  /**
   * Fetches billed competences for contract/subestipulante.
   * Backend: /v1/bff/vida/segurado/obter-competencias-faturadas
   */
  async getPaymentInvoiceCompetence(
    params: PaymentInvoiceCompetenceParams
  ): Promise<PaymentInvoiceCompetenceResponse> {
    const { identContrato, identificadorSubestipulante } = params;

    const searchParams = new URLSearchParams();
    searchParams.append('identContrato', String(identContrato));
    if (identificadorSubestipulante !== undefined) {
      searchParams.append('identificadorSubestipulante', String(identificadorSubestipulante));
    }

    const url = `${VIDA_PAYMENT_ROUTES.GET_PAYMENT_INVOICE_COMPETENCE}?${searchParams.toString()}`;

    return this.fetch<PaymentInvoiceCompetenceResponse>(url, {
      method: 'GET',
    });
  }

  /**
   * Fetches invoice detail (paginated; use first item for modal).
   * Backend: /v1/bff/vida/obter-dados-fatura
   */
  async getPaymentInvoiceDetail(
    identCobrancaObt: number,
    page?: number,
    size?: number
  ): Promise<PaymentInvoiceDetailResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('identCobrancaObt', String(identCobrancaObt));
    if (page !== undefined) {
      searchParams.append('page', String(page));
    }
    if (size !== undefined) {
      searchParams.append('size', String(size));
    }
    const url = `${VIDA_PAYMENT_ROUTES.GET_PAYMENT_INVOICE_DETAIL}?${searchParams.toString()}`;
    return this.fetch<PaymentInvoiceDetailResponse>(url, {
      method: 'GET',
    });
  }
}

